import React, { useState } from 'react';
import { MapPin, CheckSquare, Users, Activity, Menu, X } from 'lucide-react';

/**
 * Tab Navigation Component
 * Top-level navigation for the application tabs
 *
 * @param {string} activeTab - Currently active tab ('routes', 'tasks', 'leads', 'tracking')
 * @param {function} onTabChange - Callback when tab changes
 */
const TabNavigation = ({ activeTab, onTabChange }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const tabs = [
        {
            id: 'routes',
            label: 'Routes',
            icon: MapPin,
            description: 'Plan and optimize routes'
        },
        {
            id: 'tasks',
            label: 'Tasks',
            icon: CheckSquare,
            description: 'Manage tasks'
        },
        {
            id: 'leads',
            label: 'Leads Follow-ups',
            icon: Users,
            description: 'Follow up with leads'
        },
        {
            id: 'tracking',
            label: 'Tracking',
            icon: Activity,
            description: 'Track activity'
        }
    ];

    const handleTabClick = (tabId) => {
        onTabChange(tabId);
        setMobileMenuOpen(false); // Close mobile menu after selection
    };

    return (
        <>
            {/* Desktop Navigation */}
            <nav className="hidden md:block bg-white border-b-2 border-gray-200 shadow-sm">
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo/Title */}
                        <div className="flex-shrink-0 flex items-center">
                            <h1 className="text-xl font-bold text-gray-800">
                                GreenTech Shredding
                            </h1>
                        </div>

                        {/* Tab buttons */}
                        <div className="flex space-x-1">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;

                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabClick(tab.id)}
                                        className={`
                                            flex items-center gap-2 px-6 py-3 rounded-t-lg font-semibold text-sm transition-all
                                            ${
                                                isActive
                                                    ? 'bg-green-600 text-white shadow-md transform scale-105'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                                            }
                                        `}
                                        title={tab.description}
                                    >
                                        <Icon size={20} />
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Right side (placeholder for future features like user menu) */}
                        <div className="flex items-center">
                            <span className="text-sm text-gray-600">
                                {new Date().toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </span>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation */}
            <nav className="md:hidden bg-white border-b-2 border-gray-200 shadow-sm">
                <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                        {/* Logo/Title */}
                        <h1 className="text-lg font-bold text-gray-800">GreenTech</h1>

                        {/* Current tab indicator */}
                        <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                            {React.createElement(
                                tabs.find((t) => t.id === activeTab)?.icon || MapPin,
                                { size: 18 }
                            )}
                            <span>{tabs.find((t) => t.id === activeTab)?.label}</span>
                        </div>

                        {/* Hamburger menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Mobile menu dropdown */}
                    {mobileMenuOpen && (
                        <div className="mt-3 space-y-1 animate-fade-in">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;

                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabClick(tab.id)}
                                        className={`
                                            w-full flex items-center gap-3 px-4 py-3 rounded-lg font-semibold text-sm transition-all
                                            ${
                                                isActive
                                                    ? 'bg-green-600 text-white shadow-md'
                                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                            }
                                        `}
                                    >
                                        <Icon size={20} />
                                        <div className="flex-1 text-left">
                                            <div>{tab.label}</div>
                                            <div
                                                className={`text-xs ${
                                                    isActive ? 'text-green-100' : 'text-gray-500'
                                                }`}
                                            >
                                                {tab.description}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </nav>
        </>
    );
};

export default TabNavigation;
